import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import { GridToolbar } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import DownloadIcon from '@mui/icons-material/Download';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { debounce } from 'lodash';
import '../App.css';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import { fetchDeepLink, openDeepLink, buildPrizePicksSlipUrl } from '../deeplinks';
import logger from '../logger';
import PickemTutorial from './PickemTutorial';

// ── Pick'em Platform Groups ──
// Each platform maps to a prefix that matches all book name variants.
// e.g. "Underdog" matches "Underdog Fantasy (2 Pick)", "Underdog Fantasy (Multipliers)", etc.
const PICKEM_PLATFORMS = [
  { label: 'Underdog', prefix: 'Underdog', maxLegs: 8,
    // Standard payouts in American odds (all must hit)
    power: { 2: 200, 3: 500, 4: 900, 5: 1900, 6: 3400, 7: 6400, 8: 11900 },
  },
  { label: 'PrizePicks', prefix: 'PrizePicks', maxLegs: 6,
    // Power Play: all must hit (multiplier - 1 converted to American odds: 3x=+200, 6x=+500, etc.)
    power: { 2: 200, 3: 500, 4: 900, 5: 1900, 6: 3650 },
    // Flex Play: partial payouts allowed. Key = legs, value = {hits: multiplier (decimal, includes stake)}
    // e.g. 3-pick flex: 3/3 pays 3x, 2/3 pays 1.25x
    flex: {
      3: { 3: 3.0, 2: 1.25 },
      4: { 4: 6.0, 3: 1.5 },
      5: { 5: 10.0, 4: 2.0, 3: 0.4 },
      6: { 6: 25.0, 5: 2.0, 4: 0.4 },
    },
  },
  { label: 'DraftKings Pick\'em', prefix: 'DraftKings (Pick', maxLegs: 8, power: null },
  { label: 'Sleeper', prefix: 'Sleeper', maxLegs: 8, power: null },
];

// ── Multiplier-product breakeven adjustment ──
// On multiplier products (Underdog Multipliers, PrizePicks Demons & Goblins,
// DraftKings Pick 6 Multipliers), the feed's breakEvenWin reflects each leg's
// boost as if it were a standalone bet (e.g. a 1.06x boost → BE ~48.5%, +106).
// In reality a multiplier leg can only be realized inside a parlay, where its
// effective per-leg breakeven becomes (parlay_neutral_BE / leg_multiplier).
// We use a flat 0.54 neutral reference across all pickem platforms — the per-leg
// fair BE varies only ~3 percentage points across pick counts (2-pick to 8-pick)
// on UD/PP, so a single constant is a reasonable parlay-agnostic default for
// row-level display. The override is NOT applied to standard Power Play rows
// (UD 2/3/6 Pick, PP standard, Sleeper, DK Pick 2/3/6), since those carry the
// same flat per-leg price for every row anyway.
const PICKEM_NEUTRAL_BE = 0.54;

const MULTIPLIER_BOOK_PATTERNS = ['Multipliers', 'Demons and Goblins'];

const isMultiplierBook = (bookName) =>
  !!bookName && MULTIPLIER_BOOK_PATTERNS.some(p => bookName.includes(p));

// Recover the leg's multiplier from the feed's breakEvenWin
// (decimal = 1/BE; multiplier = decimal - 1) and return parlay-aware BE.
// Falls back to the feed value on bad inputs.
const pickemAdjustedBE = (bookName, feedBE) => {
  if (feedBE == null || feedBE <= 0) return feedBE;
  if (!isMultiplierBook(bookName)) return feedBE;
  const decimal = 1 / feedBE;
  const multiplier = decimal - 1;
  if (!(multiplier > 0)) return feedBE;
  return PICKEM_NEUTRAL_BE / multiplier;
};

// Compute probability of exactly k correct picks out of n with per-leg trueWin values.
// Enumerates all 2^n outcomes (max 64 for 6 legs — trivial).
function probExactlyK(trueWins, k) {
  const n = trueWins.length;
  let prob = 0;
  const total = 1 << n; // 2^n
  for (let mask = 0; mask < total; mask++) {
    let hits = 0;
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) hits++;
    }
    if (hits !== k) continue;
    let p = 1;
    for (let i = 0; i < n; i++) {
      p *= (mask & (1 << i)) ? trueWins[i] : (1 - trueWins[i]);
    }
    prob += p;
  }
  return prob;
}

const AddToBetSlipCell = React.memo(({ row, betSlip, setBetSlip, maxLegs }) => {
  const id = row.id;
  const isAdded = betSlip.includes(id);
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <IconButton
        onClick={() => {
          if (betSlip.length >= maxLegs && !isAdded) return;
          const updated = isAdded
            ? betSlip.filter(x => x !== id)
            : [...betSlip, id];
          setBetSlip(updated);
        }}
        disabled={betSlip.length >= maxLegs && !isAdded}
        size="small"
        sx={{ padding: 0, color: isAdded ? 'green' : 'gray', '&.Mui-disabled': { color: 'gray' } }}
      >
        {isAdded ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      </IconButton>
    </Box>
  );
});

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const PickemDataGrid = () => {
  const bets = useStoreData('odds');
  const sportsbooks = useStoreData('sportsbooks');
  const leaguesConfig = useStoreData('leagues') || [];
  const marketsConfig = useStoreData('markets') || [];
  const { bookLogos, teamMap } = useLogoMaps();
  // State
  const [sortModel, setSortModel] = useState([{ field: 'calculatedEV', sort: 'desc' }]);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [leagueFilter, setLeagueFilter] = useState(() => {
    const saved = localStorage.getItem('pickemLeagueFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [betTypeFilter, setBetTypeFilter] = useState(() => {
    const saved = localStorage.getItem('pickemBetTypeFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [bookFilter, setBookFilter] = useState(() => {
    const saved = localStorage.getItem('pickemBookFilter');
    return saved || '';
  });
  const [dateFilter, setDateFilter] = useState(() => {
    const saved = localStorage.getItem('pickemDateFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [betSlip, setBetSlip] = useState([]);
  const [playType, setPlayType] = useState('power'); // 'power' or 'flex'
  const [slipLoading, setSlipLoading] = useState(false);
  const gridContainerRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);

  // Cache for associated bets
  const associatedBetsCache = useRef(new Map());

  // Clear bet slip when platform filter changes
  useEffect(() => {
    setBetSlip([]);
    setPlayType('power');
    if (bookFilter) {
      setSortModel([{ field: 'calculatedEV', sort: 'desc' }]);
    }
  }, [bookFilter]);

  // Save leagueFilter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pickemLeagueFilter', JSON.stringify(leagueFilter));
  }, [leagueFilter]);

  // Save betTypeFilter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pickemBetTypeFilter', JSON.stringify(betTypeFilter));
  }, [betTypeFilter]);

  // Save bookFilter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pickemBookFilter', bookFilter);
  }, [bookFilter]);

  // Save dateFilter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pickemDateFilter', JSON.stringify(dateFilter));
  }, [dateFilter]);

  // Calculate grid width on mount and resize
  useEffect(() => {
    const updateGridWidth = debounce(() => {
      if (gridContainerRef.current) {
        setGridWidth(gridContainerRef.current.offsetWidth);
      }
    }, 100);
    updateGridWidth();
    window.addEventListener('resize', updateGridWidth);
    return () => {
      window.removeEventListener('resize', updateGridWidth);
      updateGridWidth.cancel();
    };
  }, []);

  // Process bets and filter for pick'em bets using sportsbooks config
  // ── Pickem book list from sportsbooks config (pickem=1) ──
  const pickemBooks = useMemo(() => {
    return sportsbooks.filter(sb => sb.pickem).map(sb => sb.name).filter(Boolean).sort();
  }, [sportsbooks]);

  const pickemBookNames = useMemo(() => new Set(pickemBooks), [pickemBooks]);

  const processedBets = useMemo(() => {
    if (!Array.isArray(bets)) {
      logger.log('Pickem bets prop is not an array:', bets);
      return [];
    }

    // Filter bets whose bookName matches a pickem sportsbook
    const pickemBets = bets.filter(bet => pickemBookNames.has(bet.bookName));

    const processed = pickemBets
      .filter(row => row.trueWin != null) // Filter out bets where Model% is null
      .map((row, idx) => {
        const id = row.id ?? `${idx}-${Date.now()}`;
        const displayName = row.playerName?.trim() || row.selection || '';
        let side = row.side || '';
        if (side === 'Over') side = 'o';
        if (side === 'Under') side = 'u';

        // Adjust breakEvenWin for multiplier-product rows only. Standard Power
        // Play rows pass through unchanged. We overwrite breakEvenWin so that
        // downstream consumers (column renderer, EV cell, CSV export, slip
        // EV) all use the adjusted value automatically. The original feed
        // value is retained as feedBreakEvenWin for diagnostic purposes.
        const adjustedBE = pickemAdjustedBE(row.bookName, row.breakEvenWin);

        // Calculate EV for sorting (using adjusted BE)
        let calculatedEV = null;
        if (row.trueWin != null && adjustedBE != null && adjustedBE > 0) {
          calculatedEV = (row.trueWin / adjustedBE) - 1;
        }

        // Derive betMarketId for associated bets grouping (cross-line: groups ALL lines for same player/market)
        // Player props: use playerId (stable across line changes)
        // Game lines: use fixture + market + side
        let expandMarketId = null;
        if (row.fixture_id && row.market_id) {
          if (row.playerId) {
            expandMarketId = `${row.fixture_id}||${row.market_id}||${row.playerId}||${row.side || ''}`;
          } else {
            expandMarketId = `${row.fixture_id}||${row.market_id}||||${row.side || ''}`;
          }
        }
        
        return {
          ...row,
          id,
          playerName: displayName,
          sidepoints: `${side}${row.points || ''}`.trim(),
          calculatedEV,
          feedBreakEvenWin: row.breakEvenWin,
          breakEvenWin: adjustedBE,
          // Map fields from transformRecord format to Pickem expected format
          marketSourceName: row.bookName || '',
          leagueName: row.league || '',
          betTypeName: row.marketName || row.market || '',
          eventShortName: `${teamMap[row.awayTeamId]?.abbreviation || teamMap[row.awayTeamId]?.name || '?'}@${teamMap[row.homeTeamId]?.abbreviation || teamMap[row.homeTeamId]?.name || '?'}`,
          expandMarketId,
        };
      });

    logger.log('Processed bets for Pickem:', processed.length, 'items');
    logger.log('Sample processed bets:', processed.slice(0, 3).map(bet => ({
      id: bet.id,
      leagueName: bet.leagueName,
      betTypeName: bet.betTypeName,
      marketSourceName: bet.marketSourceName,
      eventShortName: bet.eventShortName,
      playerName: bet.playerName,
      side: bet.side,
      points: bet.points,
      trueWin: bet.trueWin,
      betMarketId: bet.expandMarketId,
    })));

    return processed;
  }, [bets, pickemBookNames, teamMap]);

  // Date helpers for the date slate filter (uses local timezone so slate boundaries match what the user sees)
  const getLocalDateKey = (d) => {
    const date = new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const formatDateLabel = (key) => {
    // key is YYYY-MM-DD; build a Date in local time for nice formatting
    const [y, m, d] = key.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const dtMid = new Date(dt); dtMid.setHours(0,0,0,0);
    if (dtMid.getTime() === today.getTime()) return `Today (${dt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })})`;
    if (dtMid.getTime() === tomorrow.getTime()) return `Tomorrow (${dt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })})`;
    return dt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Compute filter options directly
  const filterOptions = useMemo(() => {
    // Leagues: prefer server config, fall back to data-derived
    const leagues = leaguesConfig.length > 0
      ? leaguesConfig.map(l => l.name || l.id).filter(Boolean)
      : [...new Set(processedBets.map(bet => bet.leagueName).filter(Boolean))];

    // Bet types: prefer server config, fall back to data-derived
    const betTypes = marketsConfig.length > 0
      ? marketsConfig.map(m => m.name || m.id).filter(Boolean)
      : [...new Set(processedBets.map(bet => bet.betTypeName).filter(Boolean))];

    // Dates: derive from event start times in local timezone, sorted ascending (YYYY-MM-DD strings sort correctly as text)
    const dates = [...new Set(
      processedBets
        .filter(bet => bet.eventStart)
        .map(bet => getLocalDateKey(bet.eventStart))
    )].sort();

    return {
      leagues,
      betTypes,
      dates,
    };
  }, [processedBets, leaguesConfig, marketsConfig]);

  // Set default bookFilter based on available platforms
  useEffect(() => {
    if (!bookFilter) {
      setBookFilter(PICKEM_PLATFORMS[0].label);
    }
  }, [bookFilter]);

  // Track whether the user has manually interacted with each filter.
  // Without this guard, polling-driven filterOptions changes would re-fill
  // any filter the user has cleared on the next poll cycle. handleFilterChange
  // flips the corresponding key when the user touches a dropdown.
  const userTouchedFiltersRef = useRef({ leagues: false, betTypes: false, dates: false });

  useEffect(() => {
    if (filterOptions.leagues.length > 0
        && !userTouchedFiltersRef.current.leagues
        && leagueFilter.length === 0) {
      setLeagueFilter(filterOptions.leagues);
    }
    if (filterOptions.betTypes.length > 0
        && !userTouchedFiltersRef.current.betTypes
        && betTypeFilter.length === 0) {
      setBetTypeFilter(filterOptions.betTypes);
    }
    if (filterOptions.dates.length > 0
        && !userTouchedFiltersRef.current.dates
        && dateFilter.length === 0) {
      setDateFilter(filterOptions.dates);
    }
  }, [filterOptions]);

  // Filter setters
  const debouncedSetLeagueFilter = setLeagueFilter;
  const debouncedSetBetTypeFilter = setBetTypeFilter;
  const debouncedSetDateFilter = setDateFilter;

  // Compute filtered bets
  const filteredBets = useMemo(() => {
    if (!processedBets.length) {
      logger.log('No processed bets to filter');
      return [];
    }

    let filtered = processedBets;

    // Filter out rows where calculated EV is above 8%
    filtered = filtered.filter(bet => {
      // If calculatedEV is null or undefined, keep the bet
      if (bet.calculatedEV == null) return true;
      // Hide bets where EV is above 8% (0.08 in decimal)
      return bet.calculatedEV <= 0.08;
    });

    if (leagueFilter.length > 0) {
      const leagueSet = new Set(leagueFilter);
      filtered = filtered.filter(bet => !bet.leagueName || leagueSet.has(bet.leagueName));
    }
    if (betTypeFilter.length > 0) {
      const betTypeSet = new Set(betTypeFilter);
      filtered = filtered.filter(bet => !bet.betTypeName || betTypeSet.has(bet.betTypeName));
    }
    if (dateFilter.length > 0) {
      const dateSet = new Set(dateFilter);
      filtered = filtered.filter(bet => !bet.eventStart || dateSet.has(getLocalDateKey(bet.eventStart)));
    }
    if (bookFilter) {
      const platform = PICKEM_PLATFORMS.find(p => p.label === bookFilter);
      if (platform) {
        filtered = filtered.filter(bet => !bet.marketSourceName || bet.marketSourceName.startsWith(platform.prefix));
      }
    }

    logger.log('Filtered bets for Pickem table:', filtered.length, 'items');
    logger.log('Sample filtered bets:', filtered.slice(0, 3).map(bet => ({
      id: bet.id,
      leagueName: bet.leagueName,
      betTypeName: bet.betTypeName,
      marketSourceName: bet.marketSourceName,
    })));
    return filtered;
  }, [
    processedBets,
    leagueFilter,
    betTypeFilter,
    bookFilter,
    dateFilter,
  ]);

  // Compute bets in the bet slip
  const betSlipBets = useMemo(() => {
    return processedBets.filter(bet => betSlip.includes(bet.id));
  }, [processedBets, betSlip]);

  // Calculate Odds, Payout, and EV based on the selected platform + play type
  const slipWinInfo = useMemo(() => {
    const platform = PICKEM_PLATFORMS.find(p => p.label === bookFilter);
    const maxLegs = platform?.maxLegs || 8;
    const hasFlex = !!platform?.flex;
    const minFlexLegs = hasFlex ? Math.min(...Object.keys(platform.flex).map(Number)) : 99;
    const empty = { odds: '-', payout: '-', ev: 0, maxLegs, hasFlex, minFlexLegs, hasDG: false };

    if (betSlipBets.length === 0) return empty;

    const trueWins = betSlipBets.map(bet => bet.trueWin ?? 0);
    const numLegs = trueWins.length;

    // Detect if any D&G picks are in the slip
    const hasDG = betSlipBets.some(bet =>
      (bet.marketSourceName || '').includes('Demons') || (bet.marketSourceName || '').includes('Goblins')
    );

    // Combined probability of all legs hitting
    const allCorrectProb = trueWins.reduce((acc, tw) => acc * tw, 1);

    // True odds (always shown)
    let odds = 'N/A';
    if (allCorrectProb > 0 && allCorrectProb < 1) {
      const o = Math.round(100 / allCorrectProb - 100);
      odds = o > 0 ? `+${o}` : o.toString();
    }

    let payout = null;  // "Coming Soon" — payout calculator temporarily disabled
    let ev = 0;

    return { odds, payout, ev, maxLegs, hasFlex, minFlexLegs, hasDG };
  }, [betSlipBets, bookFilter, playType]);

  // Auto-reset to Power if user removes legs below Flex minimum
  useEffect(() => {
    if (playType === 'flex' && betSlipBets.length < slipWinInfo.minFlexLegs) {
      setPlayType('power');
    }
  }, [betSlipBets.length, playType, slipWinInfo.minFlexLegs]);

  // Open combined betslip in PrizePicks app
  const openSlipInApp = useCallback(async () => {
    if (betSlipBets.length < 2) return;
    setSlipLoading(true);
    try {
      const isPrizePicks = bookFilter === 'PrizePicks';
      if (isPrizePicks) {
        const url = await buildPrizePicksSlipUrl(betSlipBets.map(b => b.id));
        if (url) openDeepLink(url);
      }
    } finally {
      setSlipLoading(false);
    }
  }, [betSlipBets, bookFilter]);

  useEffect(() => {
    setCurrentPage(0);
  }, [
    leagueFilter,
    betTypeFilter,
    bookFilter,
    processedBets,
  ]);

  const handleFilterChange = (e, options, setter, debouncedSetter, touchKey) => {
    if (touchKey && userTouchedFiltersRef.current.hasOwnProperty(touchKey)) {
      userTouchedFiltersRef.current[touchKey] = true;
    }
    const val = e.target.value;
    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = prev.length === options.length ? [] : options;
        debouncedSetter(newValue);
        return newValue;
      });
    } else {
      debouncedSetter(val);
    }
  };

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple (${sel.length})` : sel;
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (!filteredBets.length) return;

    const headers = [
      'League',
      'Event',
      'Event Start',
      'Participant',
      'Bet Type',
      'Line',
      'Model%',
      'Breakeven Win',
      'EV'
    ];

    const rows = filteredBets.map(bet => {
      const eventStart = bet.eventStart
        ? new Date(bet.eventStart).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }).replace(',', '')
        : '';
      const model = bet.trueWin == null ? '' : `${(bet.trueWin * 100).toFixed(1)}%`;
      
      let breakEvenWin = '';
      let evValue = '';
      
      breakEvenWin = bet.breakEvenWin == null ? '' : `${(bet.breakEvenWin * 100).toFixed(1)}%`;
      if (bet.trueWin != null && bet.breakEvenWin != null && bet.breakEvenWin > 0) {
        const ev = (bet.trueWin / bet.breakEvenWin) - 1;
        evValue = `${(ev * 100).toFixed(1)}%`;
      }

      return [
        bet.leagueName || '',
        bet.eventShortName || '',
        eventStart,
        bet.playerName || '',
        bet.betTypeName || '',
        bet.sidepoints || '',
        model,
        breakEvenWin,
        evValue,
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `pickem_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Static column definitions
  const staticColumns = [
    {
      field: 'eventShortName',
      headerName: 'Event',
      flex: 2,
      minWidth: 180,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Event
        </Typography>
      ),
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        const [awayTeam, homeTeam] = params.value.split('@');
        const leagueName = params.row.leagueName || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {/* League name */}
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0 }}>
                    {leagueName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mx: 0.5 }}>
                   |
                  </Typography>
                </>
              )}
              {/* Away team */}
              {teamMap[params.row.awayTeamId]?.logo && (
                <img
                  src={teamMap[params.row.awayTeamId]?.logo}
                  alt={`${awayTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{awayTeam}</Typography>
              <Typography sx={{ fontSize: '12px', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {/* Home team */}
              {teamMap[params.row.homeTeamId]?.logo && (
                <img
                  src={teamMap[params.row.homeTeamId]?.logo}
                  alt={`${homeTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      flex: 2,
      minWidth: 200,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Bet Info
        </Typography>
      ),
      renderCell: (params) => {
        const playerName = params.row.playerName || '';
        const betTypeName = params.row.betTypeName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={playerName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  paddingLeft: '8px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {playerName}
              </Typography>
            </Tooltip>
            <Tooltip title={betTypeName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  paddingLeft: '8px',
                  color: '#BBBBBB',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {betTypeName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    { 
      field: 'sidepoints', 
      headerName: 'Line', 
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Line
        </Typography>
      ),
      renderCell: params => {
        const sidepoints = params.value || '';
        let prefix = sidepoints[0] || ''; // 'u' or 'o'
        let numberStr = sidepoints.slice(1); // '250.5' or '20' or '2.5'
        let formattedNumber = '';
        let paddedPrefix = '      '; // Default spaces for alignment
    
        if (sidepoints && numberStr) {
          const numberParts = numberStr.split('.');
          const integerPart = numberParts[0] || ''; // '250' or '20' or '2'
          const decimalPart = numberParts[1] || '0'; // '5' or undefined (becomes '0')
          
          if (integerPart) { // Valid number part
            formattedNumber = `${integerPart}.${decimalPart}`; // Always includes decimal
            
            // Calculate padding based on the FORMATTED number length (which always includes .X)
            const formattedLength = formattedNumber.length;
            const maxLength = 6; // Accommodate numbers like "250.5" (5 chars + 1 for safety)
            const spaces = maxLength - formattedLength;
            paddedPrefix = spaces > 0 ? ' '.repeat(spaces) + prefix : prefix;
          }
        }
    
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', whiteSpace: 'pre', fontFamily: 'monospace' }}>
              {sidepoints ? `${paddedPrefix}${formattedNumber}` : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'trueWin',
      headerName: 'Model%',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Model%
        </Typography>
      ),
      valueFormatter: v => (v == null ? '' : `${(v * 100).toFixed(1)}%`)
    },
   {
      field: 'breakEvenWin',
      headerName: 'Breakeven Win',
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Breakeven Win
        </Typography>
      ),
      renderCell: (params) => {
        const breakEvenWin = params.row.breakEvenWin;
        if (breakEvenWin == null) {
          return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
              <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>-</Typography>
            </Box>
          );
        }
        
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
            <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>
              {`${(breakEvenWin * 100).toFixed(1)}%`}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'calculatedEV',
      headerName: 'EV',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          EV
        </Typography>
      ),
      renderCell: (params) => {
        const trueWin = params.row.trueWin;
        const breakEvenWin = params.row.breakEvenWin;
        
        // Calculate EV only if both values exist and breakEvenWin > 0
        if (trueWin == null || breakEvenWin == null || breakEvenWin <= 0) {
          return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
              <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>-</Typography>
            </Box>
          );
        }
        
        // Calculate EV: (Model% / Breakeven%) - 1
        const evValue = (trueWin / breakEvenWin) - 1;
        const evPercent = (evValue * 100).toFixed(1);
        
        // Color based on positive/negative EV
        const evColor = evValue > 0 ? '#00ff00' : evValue < 0 ? '#ff0000' : '#ffffff';
        
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
            <Typography sx={{ color: evColor, fontSize: '0.875rem' }}>
              {evPercent}%
            </Typography>
          </Box>
        );
      }
    },
  ];

  // Dynamic columns
  const columns = useMemo(() => [
    ...staticColumns,
    {
      field: 'deepLink',
      headerName: '',
      sortable: false,
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      flex: 0,
      renderCell: (params) => {
        const disabled = !params.row.hasDeeplink;
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              onClick={async () => { if (!disabled) { const url = await fetchDeepLink(params.row.id); if (url) openDeepLink(url); } }}
              disabled={disabled}
              size="small"
              sx={{
                color: disabled ? '#666666' : '#00ff00',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '4px 8px',
                fontSize: '0.875rem',
                minWidth: 'auto',
                '&.Mui-disabled': { color: '#666666' },
              }}
            >
              Bet
            </Button>
          </Box>
        );
      },
    },
    {
      field: 'addToBetSlip',
      headerName: 'Slip',
      sortable: false,
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      flex: 0,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Slip</Typography>
      ),
      renderCell: p => (
        <AddToBetSlipCell
          row={p.row}
          betSlip={betSlip}
          setBetSlip={setBetSlip}
          maxLegs={slipWinInfo.maxLegs}
        />
      ),
    },
  ], [teamMap, bookLogos, betSlip, slipWinInfo.maxLegs]);

  // Precompute associated bets with non-pick'em bets only
  const associatedBetsMap = useMemo(() => {
    const map = new Map();
    bets.forEach((bet, idx) => {
      // Derive expandMarketId: groups across ALL lines for same player/market
      let expandMarketId = null;
      if (bet.fixture_id && bet.market_id) {
        if (bet.playerId) {
          expandMarketId = `${bet.fixture_id}||${bet.market_id}||${bet.playerId}||${bet.side || ''}`;
        } else {
          expandMarketId = `${bet.fixture_id}||${bet.market_id}||||${bet.side || ''}`;
        }
      }
      if (!expandMarketId) return;
      // Exclude pickem books — we want to compare against regular books
      if (pickemBookNames.has(bet.bookName)) return;
      const group = map.get(expandMarketId) || [];
      group.push({ bet: { ...bet, marketSourceName: bet.bookName, expandMarketId }, idx });
      map.set(expandMarketId, group);
    });
    const result = new Map();
    map.forEach((group, expandMarketId) => {
      const betsWithSource = group.map(({ bet, idx }) => ({ ...bet, originalIndex: idx }));
      const sourceMap = new Map();
      betsWithSource.forEach(bet => {
        const source = bet.marketSourceName;
        const list = sourceMap.get(source) || [];
        list.push(bet);
        sourceMap.set(source, list);
      });
      const sortedSources = [...sourceMap.keys()].sort();
      result.set(expandMarketId, { sourceMap, sortedSources });
    });
    return result;
  }, [bets, pickemBookNames]);

  // Lazy computation for associated bets
  const getAssociatedBets = useCallback((row) => {
    if (!row.expandMarketId) return [];
    const cacheKey = `${row.expandMarketId}-${row.marketSourceName}`;
    if (associatedBetsCache.current.has(cacheKey)) {
      return associatedBetsCache.current.get(cacheKey);
    }
    const data = associatedBetsMap.get(row.expandMarketId);
    if (!data) return [];
    const { sourceMap, sortedSources } = data;
    const associated = [];

    sortedSources.forEach(source => {
      const betsForSource = sourceMap.get(source);
      associated.push(...betsForSource);
    });

    // Sort by best line then best odds (matching EVTool logic)
    const isOver = associated[0]?.side === 'Over';
    const sortedBets = associated.sort((a, b) => {
      const lineA = Number(a.points) || 0, lineB = Number(b.points) || 0;
      const priceA = a.price ?? -Infinity, priceB = b.price ?? -Infinity;
      if (lineA !== lineB) return isOver ? lineA - lineB : lineB - lineA;
      return priceB - priceA;
    });

    associatedBetsCache.current.set(cacheKey, sortedBets);
    return sortedBets;
  }, [associatedBetsMap]);

  // Clear cache periodically
  useEffect(() => {
    const interval = setInterval(() => {
      associatedBetsCache.current.clear();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Memoized detail panel item
  const DetailPanelItem = React.memo(({ index, style, data }) => {
    const bet = data.bets[index];
    const side = bet.side === 'Over' ? 'o' : bet.side === 'Under' ? 'u' : '';
    const line = bet.points != null ? bet.points : '';
    let displayOdds = bet.price != null
      ? (bet.price < 0 ? `${bet.price}` : `+${bet.price}`)
      : 'N/A';

    return (
      <Box
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: '0 11px',
          border: 'none',
          borderRadius: '4px',
          width: '100%',
        }}
      >
        <Box sx={{ mr: 0.5, transform: 'translateY(3px)' }}>
          <img
          src={bookLogos[bet.bookName] || ''}
          alt="Book Logo"
          style={{ width: 44, height: 44, objectFit: 'contain' }}
          loading="lazy"
          onError={(e) => {
            e.target.style.display = 'none'; // Hide broken images
          }}
        />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap'}}>
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              mr: 0.5,
              whiteSpace: 'nowrap',
              lineHeight: '44px',
              fontSize: '0.875rem',
            }}
          >
            {(side + line) || '-'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              whiteSpace: 'nowrap',
              lineHeight: '44px',
              fontSize: '0.875rem',
            }}
          >
            {displayOdds}
          </Typography>
        </Box>
      </Box>
    );
  });

  // Detail panel content
  const getDetailPanelContent = useCallback(({ row }) => {
    const associatedBets = getAssociatedBets(row);
    
    if (!associatedBets.length) {
      return (
        <Box sx={{ p: 1, color: '#ffffff' }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>No other books have this bet.</Typography>
        </Box>
      );
    }

    const fixedItemWidth = 150;

    return (
      <Box
        sx={{
          p: '2px 0',
          backgroundColor: '#2a2a2a',
          borderRadius: '4px',
          overflowX: 'auto',
          overflowY: 'hidden',
          display: 'flex',
          flexWrap: 'nowrap',
          height: 52,
        }}
      >
        {associatedBets.map((bet, index) => (
          <Box
            key={index}
            sx={{
              width: fixedItemWidth,
              flexShrink: 0,
              flexGrow: 0,
              display: 'flex',
              alignItems: 'center',
              borderRight: '1px solid #ffffff',
              pr: '11px',
              backgroundColor: '#2a2a2a',
            }}
          >
            <DetailPanelItem
              index={index}
              data={{ bets: associatedBets }}
            />
          </Box>
        ))}
      </Box>
    );
  }, [getAssociatedBets, gridWidth]);

  const getDetailPanelHeight = useCallback(() => 52, []);

  const handleDetailPanelExpanded = useCallback((newExpandedRowIds) => {
    setExpandedRowIds(new Set(newExpandedRowIds));
  }, []);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Filter Section (Dropdowns) */}
          <Box className="filter-section">
            <PickemTutorial />
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                {/* League Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="league-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
                  <Select
                    multiple
                    labelId="league-filter-label"
                    label="League"
                    notched
                    value={leagueFilter}
                    onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, debouncedSetLeagueFilter, 'leagues')}
                    renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filterOptions.leagues.map(o => (
                      <MenuItem key={o} value={o}>
                        <Checkbox checked={leagueFilter.includes(o)} />
                        <ListItemText primary={o} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Bet Type Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="bet-type-filter-label" shrink sx={{ color: '#ffffff' }}>Bet Type</InputLabel>
                  <Select
                    multiple
                    labelId="bet-type-filter-label"
                    label="Bet Type"
                    notched
                    value={betTypeFilter}
                    onChange={e => handleFilterChange(e, filterOptions.betTypes, setBetTypeFilter, debouncedSetBetTypeFilter, 'betTypes')}
                    renderValue={sel => renderFilterValue(sel, filterOptions.betTypes, 'Bet Type')}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={betTypeFilter.length === filterOptions.betTypes.length} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filterOptions.betTypes.map(o => (
                      <MenuItem key={o} value={o}>
                        <Checkbox checked={betTypeFilter.includes(o)} />
                        <ListItemText primary={o} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Platform Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="book-filter-label" shrink sx={{ color: '#ffffff' }}>Platform</InputLabel>
                  <Select
                    labelId="book-filter-label"
                    label="Platform"
                    notched
                    value={bookFilter}
                    onChange={e => setBookFilter(e.target.value)}
                  >
                    {PICKEM_PLATFORMS.map(p => (
                      <MenuItem key={p.label} value={p.label}>
                        <ListItemText primary={p.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Date Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="date-filter-label" shrink sx={{ color: '#ffffff' }}>Date</InputLabel>
                  <Select
                    multiple
                    labelId="date-filter-label"
                    label="Date"
                    notched
                    value={dateFilter}
                    onChange={e => handleFilterChange(e, filterOptions.dates, setDateFilter, debouncedSetDateFilter, 'dates')}
                    renderValue={sel => {
                      if (!sel.length) return 'Date';
                      if (sel.length === filterOptions.dates.length) return 'All';
                      if (sel.length === 1) return formatDateLabel(sel[0]);
                      return `Multiple (${sel.length})`;
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={dateFilter.length === filterOptions.dates.length && filterOptions.dates.length > 0} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filterOptions.dates.map(o => (
                      <MenuItem key={o} value={o}>
                        <Checkbox checked={dateFilter.includes(o)} />
                        <ListItemText primary={formatDateLabel(o)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Export to CSV">
                  <IconButton
                    onClick={exportToCSV}
                    sx={{
                      color: '#bbbbbb',
                      '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={0} sx={{ height: '100%' }}>
              {/* Table */}
              <Box sx={{ width: '75%' }}>
                <Box className="table-section">
                  <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }} ref={gridContainerRef}>
                    <DataGridPro
                      rows={filteredBets}
                      columns={columns}
                      pageSize={pageSize}
                      onPageSizeChange={setPageSize}
                      rowsPerPageOptions={[50, 100, 250]}
                      pagination
                      paginationMode="client"
                      page={currentPage}
                      onPageChange={setCurrentPage}
                      rowCount={filteredBets.length}
                      sortingMode="client"
                      sortModel={sortModel}
                      onSortModelChange={newModel => setSortModel(newModel)}
                      disableSelectionOnClick
                      disableColumnFilter
                      disableColumnMenu
                      loading={false}
                      getRowId={row => row.id}
                      getRowClassName={(params) => params.row.isDetail ? 'detail-row' : ''}
                      getDetailPanelContent={getDetailPanelContent}
                      getDetailPanelHeight={getDetailPanelHeight}
                      detailPanelExpandedRowIds={expandedRowIds}
                      onDetailPanelExpandedRowIdsChange={handleDetailPanelExpanded}
                      hideFooterSelectedRowCount
                      initialState={{
                        columns: { columnVisibilityModel: { marketSourceName: false } },
                      }}
                      components={{ Toolbar: GridToolbar }}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#1e1e1e',
                          borderBottom: '2px solid #444',
                          position: 'sticky',
                          top: '56px',
                          zIndex: 2,
                        },
                        '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
   
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                          borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
                        },
                        '& .MuiDataGrid-cell:last-child': {
                          borderRight: 'none', // Remove border from last column
                        },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                        '& .MuiDataGrid-row.Mui-selected': {
                          backgroundColor: '#1a1a1a',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                        },
                        '& .MuiDataGrid-columnSeparator': { 
                          visibility: 'visible',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': { color: '#ffffff !important' },
                        '& .MuiDataGrid-iconButtonContainer': { color: '#ffffff !important' },
                        '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                        '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          color: '#ffffff !important',
                        },
                        '& .MuiSelect-icon': { color: '#ffffff !important' },
                        '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                        '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                        '& .MuiDataGrid-root': {
                          overflowY: 'auto !important',
                          margin: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          overflowY: 'auto !important',
                        },
                        '& .MuiDataGrid-main': {
                          padding: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          backgroundColor: '#1e1e1e',
                          color: '#ffffff !important',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 1,
                          boxSizing: 'border-box',
                        },
                      }}
                      rowBuffer={10}
                      rowThreshold={5}
                    />
                  </Box>
                </Box>
              </Box>
              {/* Bet Slip Panel */}
              <Box data-tutorial="pickem-slip-panel" sx={{ width: '25%', height: 'calc(100vh - 128px)', boxSizing: 'border-box' }}>
                <Box sx={{ backgroundColor: '#1a1a1a', borderRadius: '12px', height: '100%', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                      Bet Slip ({betSlipBets.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      sx={{
                        color: '#ffffff',
                        borderColor: '#ffffff',
                        fontSize: '0.8rem',
                        padding: '4px 8px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: '#ffffff',
                        },
                      }}
                      onClick={() => setBetSlip([])}
                      disabled={betSlipBets.length === 0}
                    >
                      Clear Bet Slip
                    </Button>
                  </Stack>
                  {/* Power / Flex toggle — only shown for platforms with Flex support */}
                  {slipWinInfo.hasFlex && (
                    <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1 }}>
                      <Button
                        size="small"
                        variant={playType === 'power' ? 'contained' : 'outlined'}
                        onClick={() => setPlayType('power')}
                        sx={{
                          flex: 1, fontSize: '0.8rem', fontWeight: 600,
                          color: playType === 'power' ? '#000' : '#ffffff',
                          backgroundColor: playType === 'power' ? '#ffffff' : 'transparent',
                          borderColor: '#555',
                          '&:hover': { backgroundColor: playType === 'power' ? '#dddddd' : 'rgba(255,255,255,0.1)', borderColor: '#888' },
                        }}
                      >
                        Power
                      </Button>
                      <Button
                        size="small"
                        variant={playType === 'flex' ? 'contained' : 'outlined'}
                        onClick={() => setPlayType('flex')}
                        disabled={betSlipBets.length < slipWinInfo.minFlexLegs}
                        sx={{
                          flex: 1, fontSize: '0.8rem', fontWeight: 600,
                          color: playType === 'flex' ? '#000' : '#ffffff',
                          backgroundColor: playType === 'flex' ? '#ffffff' : 'transparent',
                          borderColor: '#555',
                          '&:hover': { backgroundColor: playType === 'flex' ? '#dddddd' : 'rgba(255,255,255,0.1)', borderColor: '#888' },
                          '&.Mui-disabled': { color: '#555', borderColor: '#333' },
                        }}
                      >
                        Flex {betSlipBets.length < slipWinInfo.minFlexLegs ? `(${slipWinInfo.minFlexLegs}+ picks)` : ''}
                      </Button>
                    </Stack>
                  )}
                  <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
                    {betSlipBets.length === 0 ? (
                      <Typography sx={{ color: '#bbbbbb', textAlign: 'center', fontStyle: 'italic' }}>
                        No bets added to the slip.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {betSlipBets.map(bet => (
                          <Box
                            key={bet.id}
                            sx={{
                              p: 1.5,
                              backgroundColor: '#222222',
                              borderRadius: '8px',
                              border: '1px solid #333',
                              transition: 'background-color 0.2s',
                              '&:hover': { backgroundColor: '#2a2a2a' },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <IconButton
                                size="small"
                                onClick={() => setBetSlip(betSlip.filter(id => id !== bet.id))}
                                sx={{ color: '#ffffff', '&:hover': { color: '#ff4444' } }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500 }}>
                                  {bet.playerName} | {bet.betTypeName} | {bet.sidepoints}
                                  {(bet.marketSourceName || '').includes('Demons') || (bet.marketSourceName || '').includes('Goblins')
                                    ? <span style={{ color: '#ff9800', fontSize: '0.75rem', marginLeft: '6px' }}>D&G</span>
                                    : null}
                                </Typography>
                                <Typography sx={{ color: '#cccccc', fontSize: '0.85rem' }}>
                                  {bet.leagueName} | {bet.eventShortName}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ color: '#cccccc', fontSize: '0.85rem', textAlign: 'right' }}>
                                  Model%: <span style={{ color: '#ffffff' }}>{((bet.trueWin || 0) * 100).toFixed(1)}%</span>
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                  {/* Fixed Odds/Payout/EV Section at the Bottom */}
                  <Box
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      backgroundColor: '#222222',
                      borderTop: '2px solid #444',
                      p: 2,
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500, mb: 1 }}>
                      True Odds: <span style={{ color: '#ffffff' }}>{slipWinInfo.odds}</span>
                    </Typography>
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500, mb: 1 }}>
                      Payout: <span style={{ color: '#888', fontStyle: 'italic' }}>Coming Soon</span>
                    </Typography>
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500 }}>
                      Slip EV: <span style={{ color: '#888', fontStyle: 'italic' }}>Coming Soon</span>
                    </Typography>
                    {bookFilter === 'PrizePicks' && betSlipBets.length >= 2 && (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={openSlipInApp}
                        disabled={slipLoading}
                        sx={{
                          mt: 1.5,
                          backgroundColor: '#7b2fbe',
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          textTransform: 'none',
                          '&:hover': { backgroundColor: '#6a28a6' },
                          '&.Mui-disabled': { backgroundColor: '#444', color: '#888' },
                        }}
                      >
                        {slipLoading ? 'Loading...' : `Open in PrizePicks (${betSlipBets.length} picks)`}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default PickemDataGrid;